import { useEffect, useState } from "react";
import { Loader2, Plus, Check, X, Users, Clock } from "lucide-react";
import api from "@/lib/api";
import { fmtMoney, errMsg, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Family approval workflow page.
 *
 * Lets the user request approval for large purchases and lets family members
 * approve/reject pending requests in the same household.
 * Requires Family / Lifetime plan or a comp grant; the server enforces this.
 */
export default function ApprovalsPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const hasAccess =
    user?.effective_tier === "family" ||
    user?.effective_tier === "lifetime" ||
    user?.is_comped;

  const load = () => {
    setLoading(true);
    api.get("/approvals")
      .then(r => setList(r.data || []))
      .catch(e => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (hasAccess) load(); else setLoading(false); }, [hasAccess]);

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="rounded-3xl border border-violet-400/20 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/5 p-10 text-center" data-testid="approvals-upsell">
          <Users className="w-12 h-12 mx-auto text-violet-300 mb-3" />
          <h2 className="text-2xl font-bold">Aprobaciones familiares</h2>
          <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
            Disponible en el plan Familia. Pide aprobación a tu pareja para gastos grandes antes de hacerlos.
          </p>
          <a
            href="/perfil"
            data-testid="approvals-upgrade-btn"
            className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-semibold hover:opacity-90"
          >
            Actualizar a Familia
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-5 sm:p-6 space-y-6" data-testid="approvals-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-violet-300 font-bold">FAMILIA</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold">Solicitudes de aprobación</h1>
          <p className="text-sm text-slate-400 mt-1">Pide aprobación de tu pareja antes de hacer un gasto grande.</p>
        </div>
        <button
          onClick={() => setShowForm(s => !s)}
          data-testid="new-approval-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-semibold"
        >
          <Plus className="w-4 h-4" /> Nueva solicitud
        </button>
      </header>

      {showForm && <ApprovalForm onCreated={() => { setShowForm(false); load(); }} />}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>
      ) : list.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-white/15 p-16 text-center" data-testid="approvals-empty">
          <Clock className="w-12 h-12 mx-auto text-violet-400 mb-3 opacity-60" />
          <h3 className="text-lg font-bold">No hay solicitudes aún</h3>
          <p className="mt-1 text-sm text-slate-400">Cuando alguien de tu hogar pida aprobación, aparecerá aquí.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((a) => <ApprovalCard key={a.id} a={a} currentUser={user} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}

function ApprovalForm({ onCreated }) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) { toast.error("Monto inválido"); return; }
    if (!description.trim()) { toast.error("Describe la compra"); return; }
    setBusy(true);
    try {
      await api.post("/approvals", {
        amount: Number(amount),
        currency,
        description: description.trim(),
        category: category || null,
      });
      toast.success("Solicitud enviada");
      onCreated();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 space-y-4" data-testid="approval-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <Label>Descripción</Label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ej: Laptop nueva, viaje a Punta Cana..."
            data-testid="approval-description"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm"
          />
        </div>
        <div>
          <Label>Categoría</Label>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="(opcional)"
            data-testid="approval-category"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <Label>Monto</Label>
          <input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            data-testid="approval-amount"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm"
          />
        </div>
        <div>
          <Label>Moneda</Label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            data-testid="approval-currency"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm"
          >
            {["USD", "DOP", "EUR", "USDT", "MXN", "COP"].map(c => (
              <option key={c} value={c} className="bg-[#15141b]">{c}</option>
            ))}
          </select>
        </div>
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="approval-submit"
        className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold disabled:opacity-60"
      >
        {busy ? "Enviando..." : "Enviar solicitud"}
      </button>
    </form>
  );
}

function ApprovalCard({ a, currentUser, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/approvals/${a.id}/decide`, { decision, note });
      toast.success(decision === "approved" ? "Aprobada" : "Rechazada");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const isMine = a.requested_by_email === currentUser?.email;
  const status = a.status;
  const isPending = status === "pending";

  const statusBadge =
    status === "approved" ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
    : status === "rejected" ? "bg-rose-500/15 border-rose-500/30 text-rose-300"
    : "bg-amber-500/15 border-amber-500/30 text-amber-300";

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5" data-testid={`approval-card-${a.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-white">{a.description}</div>
          <div className="text-[12px] text-slate-400 mt-1">
            {isMine ? "Tú solicitaste" : `Solicitado por ${a.requested_by_name || a.requested_by_email}`}
            {a.category && <> · {a.category}</>}
            <> · {fmtDate(a.created_at)}</>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold tabular-nums">{a.currency} {fmtMoney(a.amount)}</div>
          <span className={`mt-1 inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${statusBadge}`}>
            {status === "approved" ? "Aprobada" : status === "rejected" ? "Rechazada" : "Pendiente"}
          </span>
        </div>
      </div>

      {isPending && !isMine && (
        <div className="mt-4 space-y-2">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nota (opcional)"
            data-testid={`approval-note-${a.id}`}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs"
          />
          <div className="flex gap-2">
            <button
              onClick={() => decide("rejected")}
              disabled={busy}
              data-testid={`approval-reject-${a.id}`}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-300 text-sm font-semibold hover:bg-rose-500/25 disabled:opacity-60"
            >
              <X className="w-3.5 h-3.5" /> Rechazar
            </button>
            <button
              onClick={() => decide("approved")}
              disabled={busy}
              data-testid={`approval-approve-${a.id}`}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/30 disabled:opacity-60"
            >
              <Check className="w-3.5 h-3.5" /> Aprobar
            </button>
          </div>
        </div>
      )}

      {!isPending && a.approver_decisions?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-1">
          {a.approver_decisions.map((d, i) => (
            <div key={i} className="text-[11px] text-slate-400">
              {d.by_email} {d.decision === "approved" ? "aprobó" : "rechazó"} {d.note && <>· &ldquo;{d.note}&rdquo;</>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">{children}</div>;
}
